import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

type InvoiceStatus = 'generated' | 'sent' | 'failed';

type PaymentInvoice = {
  id: string;
  payment_id: string;
  invoice_number: string;
  recipient_email: string;
  amount_vatu: number;
  currency: string;
  issued_at: string;
  status: InvoiceStatus;
  sent_at: string | null;
  last_error: string | null;
  meta: Record<string, any>;
};

export default function InvoicesPage() {
  const { userProfile, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<PaymentInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<PaymentInvoice | null>(null);
  const [companyName, setCompanyName] = useState<string>('Innovatelhub Solutions Ltd');
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  
  // Combined invoice state for group creators
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [generatingCombined, setGeneratingCombined] = useState(false);

  const isSuperAdmin = userProfile?.role === 'super_admin';
  const isGroupCreator = userProfile?.role === 'group_creator';
  const canAccess = userProfile?.role === 'group_admin' || userProfile?.role === 'super_admin' || userProfile?.role === 'group_creator';

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('payment_invoices')
        .select(
          'id, payment_id, invoice_number, recipient_email, amount_vatu, currency, issued_at, status, sent_at, last_error, meta'
        );

      // If group_creator, only show invoices for their groups
      if (isGroupCreator && user) {
        // Get all groups created by this user
        const { data: groups, error: groupsError } = await supabase
          .from('groups')
          .select('id')
          .eq('created_by', user.id)
          .eq('is_active', true);

        if (groupsError) throw groupsError;

        const groupIds = groups?.map(g => g.id) || [];
        
        if (groupIds.length > 0) {
          // Filter invoices by payment transactions that belong to these groups
          const { data: payments, error: paymentsError } = await supabase
            .from('payment_transactions')
            .select('id')
            .in('group_id', groupIds);

          if (paymentsError) throw paymentsError;

          const paymentIds = payments?.map(p => p.id) || [];
          
          if (paymentIds.length > 0) {
            query = query.in('payment_id', paymentIds);
          } else {
            // No payments found, return empty array
            setInvoices([]);
            return;
          }
        } else {
          // No groups found, return empty array
          setInvoices([]);
          return;
        }
      } else if (!isSuperAdmin && user) {
        // For group_admin, filter by their user_id in payment_transactions
        const { data: payments, error: paymentsError } = await supabase
          .from('payment_transactions')
          .select('id')
          .eq('user_id', user.id);

        if (paymentsError) throw paymentsError;

        const paymentIds = payments?.map(p => p.id) || [];
        
        if (paymentIds.length > 0) {
          query = query.in('payment_id', paymentIds);
        } else {
          setInvoices([]);
          return;
        }
      }

      const { data, error } = await query.order('issued_at', { ascending: false });

      if (error) throw error;
      setInvoices((data || []) as PaymentInvoice[]);
    } catch (e: any) {
      console.error('Error fetching invoices:', e);
      setError(e?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    fetchInvoices();
    loadCompanyProfile();
    if (isGroupCreator && user) {
      fetchGroupsForCreator();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, isGroupCreator]);

  const fetchGroupsForCreator = async () => {
    if (!user) return;
    
    try {
      setLoadingGroups(true);
      const { data, error } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          created_at,
          subscription:user_subscriptions!user_subscriptions_user_id_fkey(
            id,
            subscription_status,
            plan:subscription_plans(
              id,
              plan_name,
              billing_model,
              price_per_member_vatu
            )
          )
        `)
        .eq('created_by', user.id)
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (error) throw error;
      
      // Calculate member count and amount for each group
      const groupsWithDetails = await Promise.all(
        (data || []).map(async (group: any) => {
          const { data: memberCount } = await supabase.rpc('get_group_member_count', {
            group_id_param: group.id
          });
          
          const isPayAsYouGo = group.subscription?.[0]?.plan?.billing_model === 'per_member';
          const pricePerMember = group.subscription?.[0]?.plan?.price_per_member_vatu || 0;
          const totalAmount = isPayAsYouGo ? (memberCount || 0) * pricePerMember : 0;
          
          return {
            ...group,
            memberCount: memberCount || 0,
            totalAmount,
            isPayAsYouGo,
            pricePerMember
          };
        })
      );
      
      setGroups(groupsWithDetails);
    } catch (e: any) {
      console.error('Error fetching groups:', e);
    } finally {
      setLoadingGroups(false);
    }
  };
  
  const handleGenerateCombinedInvoice = async () => {
    if (!user) return;
    
    if (!confirm('Generate a combined invoice for all your groups? This will create a single invoice with the total amount.')) {
      return;
    }
    
    try {
      setGeneratingCombined(true);
      const { data, error } = await supabase.rpc('generate_combined_invoice_for_creator', {
        group_creator_id_param: user.id,
        generated_by_user_id: user.id
      });
      
      if (error) throw error;
      
      if (data?.error) {
        alert('Error: ' + data.error);
        return;
      }
      
      if (data?.success) {
        alert(`Combined invoice generated successfully!\n\nInvoice Number: ${data.invoice_number}\nTotal Amount: ${data.total_amount.toLocaleString()} VT\nGroups: ${data.group_count}\nTotal Members: ${data.total_members}`);
        await fetchInvoices();
        await fetchGroupsForCreator();
      }
    } catch (e: any) {
      console.error('Error generating combined invoice:', e);
      alert('Failed to generate combined invoice: ' + (e.message || 'Unknown error'));
    } finally {
      setGeneratingCombined(false);
    }
  };

  const loadCompanyProfile = async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('company_name, company_logo_url')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setCompanyName(data.company_name || 'Innovatelhub Solutions Ltd');
        setCompanyLogoUrl(data.company_logo_url || null);
      }
    } catch (e) {
      console.error('Error loading company profile:', e);
    }
  };

  const statusBadge = (status: InvoiceStatus) => {
    const cls =
      status === 'sent'
        ? 'bg-success/10 text-success border-success/20'
        : status === 'failed'
        ? 'bg-destructive/10 text-destructive border-destructive/20'
        : 'bg-muted text-muted-foreground border-border';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium border ${cls}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const formatMoney = (amountVatu: number) => `${Number(amountVatu || 0).toLocaleString()} VT`;

  const printableHtml = useMemo(() => {
    if (!selectedInvoice) return '';

    const meta = selectedInvoice.meta || {};
    const planName = meta.plan_name || 'Subscription';
    const paymentRef = meta.payment_reference || 'N/A';
    const paymentMethod = meta.payment_method ? String(meta.payment_method).replace('_', ' ') : 'N/A';
    
    // Use company branding from user profile, fallback to default
    const displayCompanyName = companyName || 'Innovatelhub Solutions Ltd';
    const displayLogoUrl = companyLogoUrl || '/assets/images/ihslogo.png';

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Invoice ${selectedInvoice.invoice_number}</title>
    <style>
      body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 24px; }
      .muted { color: #666; }
      .row { display: flex; justify-content: space-between; margin: 8px 0; }
      .card { border: 1px solid #ddd; border-radius: 8px; padding: 16px; max-width: 680px; }
      .header { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; padding-bottom: 16px; border-bottom: 2px solid #eee; }
      .logo { width: 64px; height: 64px; object-fit: contain; }
      .company-info { flex: 1; }
      h1 { margin: 0 0 4px; font-size: 20px; }
      .company-name { margin: 0; font-size: 14px; color: #666; }
      hr { border: none; border-top: 1px solid #eee; margin: 16px 0; }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="header">
        <img src="${displayLogoUrl}" alt="${displayCompanyName}" class="logo" onerror="this.src='/assets/images/ihslogo.png'" />
        <div class="company-info">
          <h1>Nasuman Invoice</h1>
          <p class="company-name">${displayCompanyName}</p>
        </div>
      </div>
      <div class="muted">Invoice Number: <b>${selectedInvoice.invoice_number}</b></div>
      <div class="muted">Issued: ${new Date(selectedInvoice.issued_at).toLocaleString()}</div>
      <hr />
      <div class="row"><div class="muted">Recipient Email</div><div><b>${selectedInvoice.recipient_email}</b></div></div>
      <div class="row"><div class="muted">Plan</div><div>${planName}</div></div>
      <div class="row"><div class="muted">Amount</div><div><b>${formatMoney(selectedInvoice.amount_vatu)}</b></div></div>
      <div class="row"><div class="muted">Currency</div><div>${selectedInvoice.currency || 'VUV'}</div></div>
      <hr />
      <div class="row"><div class="muted">Payment Reference</div><div>${paymentRef}</div></div>
      <div class="row"><div class="muted">Payment Method</div><div>${paymentMethod}</div></div>
      <div class="row"><div class="muted">Status</div><div>${String(selectedInvoice.status).toUpperCase()}</div></div>
      <div class="row"><div class="muted">Sent At</div><div>${selectedInvoice.sent_at ? new Date(selectedInvoice.sent_at).toLocaleString() : 'Not sent'}</div></div>
      ${selectedInvoice.last_error ? `<hr /><div class="muted"><b>Last error:</b> ${selectedInvoice.last_error}</div>` : ''}
    </div>
    <script>window.onload = () => { window.focus(); }</script>
  </body>
</html>`;
  }, [selectedInvoice, companyName, companyLogoUrl]);

  const handlePrint = () => {
    if (!printableHtml) return;

    // Printing via popup windows is often blocked or renders blank pages in some browsers.
    // Use a hidden iframe to print reliably.
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc || !iframe.contentWindow) {
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(printableHtml);
    doc.close();

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch {
        // ignore
      }
    };

    // Give the browser a tick to render content before printing.
    setTimeout(() => {
      try {
        iframe.contentWindow!.focus();
        iframe.contentWindow!.print();
      } finally {
        // Cleanup after a short delay to allow the print dialog to open.
        setTimeout(cleanup, 500);
      }
    }, 50);
  };

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} isMobileMenuOpen={isMobileMenuOpen} />

      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar isCollapsed={isSidebarCollapsed} userRole="admin" isSuperAdmin={isSuperAdmin} />
        </div>

        <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-4 lg:p-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">Invoices</h1>
                <p className="text-sm text-muted-foreground">
                  {isSuperAdmin
                    ? 'All subscription payment invoices in the system.'
                    : 'Your subscription payment invoices (sent to your email).'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={fetchInvoices} iconName="RefreshCw" disabled={loading}>
                  Refresh
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Combined Invoice Section for Group Creators */}
            {isGroupCreator && (
              <div className="mb-6 bg-card border border-border rounded-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground mb-1">Pay for All Groups</h2>
                    <p className="text-sm text-muted-foreground">
                      Generate a combined invoice for all your groups on pay-as-you-go plans
                    </p>
                  </div>
                  <Button
                    onClick={handleGenerateCombinedInvoice}
                    disabled={generatingCombined || loadingGroups || groups.filter(g => g.isPayAsYouGo).length === 0}
                    iconName={generatingCombined ? 'Loader2' : 'FileText'}
                    iconPosition="left"
                  >
                    {generatingCombined ? 'Generating...' : 'Generate Combined Invoice'}
                  </Button>
                </div>
                
                {loadingGroups ? (
                  <div className="text-center py-4 text-muted-foreground">Loading groups...</div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">No active groups found</div>
                ) : (
                  <div className="space-y-2">
                    {groups.map((group) => {
                      if (!group.isPayAsYouGo) return null;
                      
                      return (
                        <div
                          key={group.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-md border border-border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{group.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {group.memberCount} members × {group.pricePerMember.toLocaleString()} VT = {group.totalAmount.toLocaleString()} VT
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {groups.filter(g => g.isPayAsYouGo).length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-foreground">Total:</span>
                          <span className="text-lg font-bold text-foreground">
                            {groups
                              .filter(g => g.isPayAsYouGo)
                              .reduce((sum, g) => sum + (g.totalAmount || 0), 0)
                              .toLocaleString()} VT
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {groups.filter(g => g.isPayAsYouGo).length} group(s) • {groups
                            .filter(g => g.isPayAsYouGo)
                            .reduce((sum, g) => sum + (g.memberCount || 0), 0)} total members
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading invoices...</div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="bg-card border border-border rounded-md p-12 text-center">
                <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-2">No invoices found</p>
                <p className="text-xs text-muted-foreground">
                  Invoices are generated when your subscription payment is approved.
                </p>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Invoice</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Issued</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                          <td className="py-3 px-4">
                            <div className="font-medium text-foreground">{inv.invoice_number}</div>
                            <div className="text-xs text-muted-foreground">
                              Ref: {inv.meta?.payment_reference || 'N/A'}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">
                            {new Date(inv.issued_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{inv.meta?.plan_name || 'Subscription'}</td>
                          <td className="py-3 px-4 text-sm font-medium text-foreground">{formatMoney(inv.amount_vatu)}</td>
                          <td className="py-3 px-4">{statusBadge(inv.status)}</td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setSelectedInvoice(inv)}
                              className="text-primary hover:text-primary/80 text-sm font-medium transition-interactive"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                      {invoices.length > 0 && (
                        <tr className="bg-muted/50 border-t-2 border-border">
                          <td colSpan={3} className="py-3 px-4 text-right">
                            <span className="text-sm font-semibold text-foreground">Total:</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm font-bold text-foreground">
                              {invoices.reduce((sum, inv) => sum + Number(inv.amount_vatu || 0), 0).toLocaleString()} VT
                            </span>
                          </td>
                          <td colSpan={2} className="py-3 px-4"></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Invoice detail modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Invoice</h2>
                <p className="text-xs text-muted-foreground">{selectedInvoice.invoice_number}</p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Issued</p>
                  <p className="text-sm text-foreground">{new Date(selectedInvoice.issued_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                  <div>{statusBadge(selectedInvoice.status)}</div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Recipient</p>
                  <p className="text-sm text-foreground">{selectedInvoice.recipient_email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Amount</p>
                  <p className="text-sm font-semibold text-foreground">{formatMoney(selectedInvoice.amount_vatu)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Plan</p>
                  <p className="text-sm text-foreground">{selectedInvoice.meta?.plan_name || 'Subscription'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payment Reference</p>
                  <p className="text-sm text-foreground">{selectedInvoice.meta?.payment_reference || 'N/A'}</p>
                </div>
              </div>

              {selectedInvoice.last_error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-xs text-destructive font-medium mb-1">Last error</p>
                  <p className="text-sm text-destructive">{selectedInvoice.last_error}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setSelectedInvoice(null)}>
                Close
              </Button>
              <Button onClick={handlePrint} iconName="Printer">
                Print
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


